import { useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import { DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { SelectedTextResult } from "@common/components/documentPreview/textSelection/textSelection.types";
import PaneLayout, { PaneTitle } from "../../../../main/panes/paneLayout/PaneLayout";
import { usePane } from "../../../../main/panes/paneLayout/usePane";
import { defineDocumentReviewPane } from "../../../../main/panes/documentPane/documentReviewPane";
import { publishStageDocumentOverlay } from "../../../../main/panes/documentPane/stageDocumentOverlay";
import { usePanes } from "../../../../main/panes/usePanes";
import ObligationExtractionPanel from "./ObligationExtractionPanel";
import RescanWithGuidanceDialog from "./rescanWithGuidanceDialog/RescanWithGuidanceDialog";
import { useObligationExtractionPanel } from "./ObligationExtractionPanel.hooks";
import { useObligationExtractionPanelStyles } from "./ObligationExtractionPanel.styles";
import { useObligationExtractionAffordance } from "./useObligationExtractionAffordance";

export interface ObligationExtractionPaneProps {
    contractId: string;
    documentId: string;
    onGoToObligations?: () => void;
}

/**
 * The obligation review as a rail occupant. It owns the scan and tells the document on the stage, through
 * the overlay channel, which clause to highlight and where picked text should go.
 */
function ObligationExtractionPane(props: ObligationExtractionPaneProps) {
    const { translateString } = useTranslation();
    const styles = useObligationExtractionPanelStyles();
    const { closeSlot, refetchPageBehindPanes } = usePanes();
    const { setHasUnsavedChanges } = usePane();
    const { isAiSpendingExhausted } = useObligationExtractionAffordance();
    const [showRescanDialog, setShowRescanDialog] = useState(false);

    // Deliberately does not close the pane on creation: the panel shows what was created and the user
    // leaves from there. Closing here would replace the whole point of the completion screen with a
    // vanishing act.
    const state = useObligationExtractionPanel({
        contractId: props.contractId,
        documentId: props.documentId,
        autoScan: true,
        onCreated: refetchPageBehindPanes,
    });

    // A review the user may have spent minutes editing is worth asking about. Before the scan returns, and
    // once every obligation is created, there is nothing to lose.
    const hasReviewInProgress = state.pages.length > 0 && !state.isTerminal;
    useEffect(() => {
        setHasUnsavedChanges(hasReviewInProgress);
    }, [hasReviewInProgress, setHasUnsavedChanges]);

    // Published only when what it says changes, not on every render of this pane: the handler goes through
    // a ref so a fresh closure per render does not re-render the document on the stage per keystroke.
    const applySelectedClause = useRef(state.applySelectedClause);
    applySelectedClause.current = state.applySelectedClause;
    const highlightedClause = state.currentClauseLocated ? state.currentClause : undefined;
    const canApplySelectedClause = state.canApplySelectedClause;
    const useAsClauseLabel = translateString("useAsClause");
    useEffect(() => {
        publishStageDocumentOverlay({
            documentId: props.documentId,
            citationChunkContent: highlightedClause,
            textSelection: canApplySelectedClause
                ? { onConfirm: (result: SelectedTextResult) => applySelectedClause.current(result), actionLabel: useAsClauseLabel }
                : undefined,
        });
    }, [props.documentId, highlightedClause, canApplySelectedClause, useAsClauseLabel]);

    useEffect(() => () => publishStageDocumentOverlay(undefined), []);

    // The obligations exist once this is offered, so both panes have served their purpose.
    const goToObligations = () => {
        closeSlot("right");
        closeSlot("stage");
        props.onGoToObligations?.();
    };

    // Until there is a result there is nothing to count and nothing worth rescanning.
    const isListing = state.hasScanned && !state.hasScanFailed && state.pages.length > 0;

    const header = isListing ? (
        <Box sx={styles.headerRow}>
            <PaneTitle>{translateString("obligationXOfY", { current: state.pageIndex + 1, total: state.pages.length })}</PaneTitle>
            <DotLegalHeader headerStyle={"extraSmall"} color={"lightgrey"} fontWeight={400} marginBottom={0}>
                {translateString("obligationsFoundCount", { count: state.pages.length })}
            </DotLegalHeader>
        </Box>
    ) : (
        <PaneTitle>{translateString("findObligationsWithAi")}</PaneTitle>
    );

    const actions = isListing ? (
        <Tooltip title={isAiSpendingExhausted ? translateString("aiSpendingLimitReached") : translateString("obligationExtractionScanAgainTooltip")}>
            <span>
                <IconButton size="small" onClick={() => setShowRescanDialog(true)} disabled={state.progress.isCreating || isAiSpendingExhausted}>
                    <Refresh fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    ) : undefined;

    return (
        // The panel's footer carries its pager and creation progress and changes with the review's state,
        // so it stays inside the panel and the panel keeps its own insets.
        <PaneLayout header={header} actions={actions} noContentPadding>
            <ObligationExtractionPanel
                state={state}
                contractId={props.contractId}
                onGoToObligations={goToObligations}
                onOpenRescanDialog={() => setShowRescanDialog(true)}
                isAiSpendingExhausted={isAiSpendingExhausted}
            />

            {/* Reachable from the header and from the empty result alike, so it hangs here rather than in
                either of them. */}
            {showRescanDialog && (
                <RescanWithGuidanceDialog
                    onDialogClose={() => setShowRescanDialog(false)}
                    onRescan={(guidance) => {
                        setShowRescanDialog(false);
                        state.rescan(guidance);
                    }}
                />
            )}
        </PaneLayout>
    );
}

export default defineDocumentReviewPane({
    slot: "right",
    component: ObligationExtractionPane,
    reviewedDocumentId: (props) => props.documentId,
});
